import AppConfig from "@/core/config/AppConfig";
import NotificationDialog from "@/core/helpers/notificationDialog";
import { toQueryStr } from "@/core/helpers/stringHelper";
import Pagination from "@/models/common/Pagination";
import JwtService from "../JwtService";
import BaseApiService from "./BaseApiService";
import ServiceConstants from "./ServiceConstants";
import { downloadFromBlob } from "@/core/helpers/fileDownloadHelper";
import store from "@/store";
import { Mutations } from "@/store/enums/StoreEnums";
import { getWord } from "../../helpers/translator";

class CustomerApiService extends BaseApiService {
    private static authenticatedClient = () => {
        const token = JwtService.getToken();
        return new BaseApiService(token).Build(AppConfig.Api.Customer.BaseUrl);
    }
    private static authenticatedClientForDownload = () => {
        const token = JwtService.getToken();
        return new BaseApiService(token).BuildForDownload(AppConfig.Api.Customer.BaseUrl);
    }
    private static authenticatedClientForFile = () => {
        const token = JwtService.getToken()
        return new BaseApiService(token, true).Build(AppConfig.Api.Customer.BaseUrl);
    }
    private static client = new BaseApiService()
        .Build(AppConfig.Api.Customer.BaseUrl);

    public static CustomerManagement = {
        Company: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Company.CompanyListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetForSelect: function () {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Company.CompanyListPath(1, 1000, null))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Company.CompanyDetailPath(id))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Company.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("companyList", "editCompany", data.data.id, false, getWord("RememberToSendDocuments"))
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Company.CompanyDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Company.CompanyDetailPath(id))
            },
            SendDocuments: function (companyId: string, selectedDocuments: string[]) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Company.SendDocumentsPath(companyId), selectedDocuments)
                .then(() => {
                    NotificationDialog.Notify("DocumentsSentSuccessfully");
                })
            }
        },
        Machine: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Machine.MachineListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetForSelect: function () {
                return BaseApiService.GetListForSelectbox(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Machine.MachineListPath(1, 1000, null))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Machine.MachineDetailPath(id))
            },
            Create: function (entity: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Machine.Name, entity).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("machineList", "editMachine", data.data.id)
                    }
                })
            },
            Update: function (entity: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Machine.MachineDetailPath(entity.id), entity).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (machineId: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.Machine.MachineDetailPath(machineId));
            }
        },
        MemberInfo:
        {
            SendResetPasswordMail: function (id: string) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.CustomerManagement.MemberInfo.SendResetPasswordMailPath(id), null)
                    .then(() => {
                        NotificationDialog.Notify("ResetPasswordEmailSent");
                    })
            }
        }
    }

    public static Licensing = {
        CompanyLicense: {
            GetByCompanyId: function (companyId: string, pagination: Pagination) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyLicenseListPath(companyId, pagination.page!, pagination.pageSize))
            },
            GetRobotLicenses: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyRobotLicensesPath(companyId))
            },
            GetRobotLicensesByRobotId: function (robotId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyRobotLicensesByRobotIdPath(robotId))
            },
            GetStudioLicenses: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyStudioLicensesPath(companyId))
            },
            GetMindPilotLicenses: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyMindPilotLicensesPath(companyId))
            },
            GetStudioAndMindPilotLicenses: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyStudioAndMindPilotLicensesPath(companyId))
            },
            GetRobotAndMindPilotLicenses: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyRobotAndMindPilotLicensesPath(companyId))
            },
            GetAllCompanyLicenses: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.GetAllCompanyLicensesPath(companyId))
            },
            GetForSelect: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyLicenseListPath(companyId, 1, 1000))
            },
            GetConsumptions: function (licenseId: string, pagination: Pagination) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.ConsumptionsPath(licenseId, pagination.page!, pagination.pageSize))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyLicenseDetailPath(id))
            },
            AddLicense: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.Name, model).then(({ data }) => {
                    if (data.data) {
                        const companyDetailRoute = { name: "viewCompany", params: { id: model.companyId } };
                        const licenseDetailRoute = { name: "viewCompanyLicense", params: { id: data.data.id } };
                        NotificationDialog.SuccessPromptRouter(companyDetailRoute, "GoToCompanyDetail", licenseDetailRoute, "GoToLicenseDetails");
                    }
                })
            },
            UpdateLicense: function (model: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyLicenseDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            DeleteLicense: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.CompanyLicenseDetailPath(id))
            },
            GetConsumption: function (consumptionId: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.ConsumptionDetailPath(consumptionId))
            },
            GetConsumptionJobs: function (consumptionId: string, pagination: Pagination) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.CompanyLicense.ConsumptionJobsPath(consumptionId, pagination.page!, pagination.pageSize));
            },
        },
        LicenseType: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseType.LicenseTypeListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetForSelect: function () {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseType.LicenseTypeListPath(1, 1000, null))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseType.LicenseTypeDetailPath(id))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseType.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("licenseTypeList", "editLicenseType", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseType.LicenseTypeDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseType.LicenseTypeDetailPath(id))
            },
        },
        LicenseDefinition: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseDefinition.LicenseDefinitionListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetForSelect: function () {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseDefinition.LicenseDefinitionListPath(1, 1000, null))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseDefinition.LicenseDefinitionDetailPath(id))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseDefinition.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("licenseDefinitionList", "editLicenseDefinition", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseDefinition.LicenseDefinitionDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Licensing.LicenseDefinition.LicenseDefinitionDetailPath(id))
            },
        }
    }

    public static Logging = {
        GetLogs: function (pagination: Pagination) {
            return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.Logging.Common.All(pagination.page!, pagination.pageSize))
        },
        GetById: function (id: string) {
            return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.Logging.Common.Single(id))
        },
        GetAuditLogs: function (pagination: Pagination, filters: any = null) {
            let path = ServiceConstants.Logging.System.AuditAll(pagination.page!, pagination.pageSize)
            if (filters) {
                const filtersQuery = toQueryStr(filters)
                if (filters && filtersQuery && filtersQuery !== "?") {
                    path = `${path}${filtersQuery}`;
                }
            }
            return BaseApiService.GetList(CustomerApiService.authenticatedClient(), path)
        },
        GetExceptionLogs: function (pagination: Pagination) {
            return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.Logging.System.ExceptionAll(pagination.page!, pagination.pageSize))
        },
        GetFatalExceptionLogs: function (pagination: Pagination) {
            return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.Logging.System.FatalExceptionAll(pagination.page!, pagination.pageSize))
        }
    }

    public static Common = {
        ValidateReCaptchaToken: function (token: string) {
            return BaseApiService.Create(CustomerApiService.client, ServiceConstants.CustomerService.Validation.ReCaptcha, { Token: token })
        },
        GetStudioReleases: function () {
            return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Releases.StudioPath(JwtService.getLanguage() ?? "en"));
        },
        DownloadInstaller: function () {
            if (store.getters.downloadPercentage.fileName != '') {
                NotificationDialog.Info(getWord("ADownloadIsAlreadyInProgress"));
                return;
            }

            store.commit(Mutations.SET_PERCENTAGE, {
                fileName: "AutomindInstaller",
                extension: 'exe',
                percent: '0.00'
            });

            return CustomerApiService.authenticatedClientForDownload().get(
                ServiceConstants.CustomerService.Releases.Download.Installer(),
                {
                    onDownloadProgress: progressEvent => {
                        const total = progressEvent.total;
                        const current = progressEvent.loaded;

                        store.commit(Mutations.SET_PERCENTAGE, {
                            fileName: 'AutomindInstaller',
                            extension: 'exe',
                            percent: (current / total * 100).toFixed(2)
                        });
                    }
                }
            )
            .then((response) => {
                if (response.data) {
                    downloadFromBlob(response.data, 'AutomindInstaller', 'exe');
                    NotificationDialog.Success('AutomindInstaller.exe ' + getWord('FileDownloadCompleted'));
                }
            })
            .catch((error) => {
                const err = new Error(error)
                BaseApiService.HandleError(error)
                throw err;
            })
            .finally(() => {
                store.commit(Mutations.SET_PERCENTAGE, {
                    fileName: '',
                    extension: '',
                    percent: '0.00'
                });
            })
        },
        DownloadStudioRelease: function (fileName: string) {
            if (fileName == store.getters.downloadPercentage.fileName)
                return;
            if (store.getters.downloadPercentage.fileName != '') {
                NotificationDialog.Info(getWord("ADownloadIsAlreadyInProgress"));
                return;
            }

            store.commit(Mutations.SET_PERCENTAGE, {
                fileName: fileName,
                extension: 'zip',
                percent: '0.00'
            });

            return CustomerApiService.authenticatedClientForDownload().get(
                ServiceConstants.CustomerService.Releases.Download.Studio(fileName),
                {
                    onDownloadProgress: progressEvent => {
                        const total = progressEvent.total;
                        const current = progressEvent.loaded;

                        store.commit(Mutations.SET_PERCENTAGE, {
                            fileName: fileName,
                            extension: 'zip',
                            percent: (current / total * 100).toFixed(2)
                        });
                    }
                }
            )
                .then((response) => {
                    if (response.data) {
                        downloadFromBlob(response.data, fileName, "zip");
                        NotificationDialog.Success(fileName + '.zip ' + getWord("FileDownloadCompleted"));
                    }
                })
                .catch((error) => {
                    const err = new Error(error)
                    BaseApiService.HandleError(error)
                    throw err;
                })
                .finally(() => {
                    store.commit(Mutations.SET_PERCENTAGE, {
                        fileName: '',
                        extension: '',
                        percent: '0.00'
                    });
                })
        },
        GetAgentReleases: function () {
            return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Releases.AgentPath(JwtService.getLanguage() ?? "en"));
        },
        DownloadAgentRelease: function (fileName: string) {
            if (fileName == store.getters.downloadPercentage.fileName)
                return;
            if (store.getters.downloadPercentage.fileName != '') {
                NotificationDialog.Info(getWord("ADownloadIsAlreadyInProgress"));
                return;
            }

            store.commit(Mutations.SET_PERCENTAGE, {
                fileName: fileName,
                extension: 'zip',
                percent: '0.00'
            });

            return CustomerApiService.authenticatedClientForDownload().get(
                ServiceConstants.CustomerService.Releases.Download.Agent(fileName),
                {
                    onDownloadProgress: progressEvent => {
                        const total = progressEvent.total;
                        const current = progressEvent.loaded;

                        const percentCompleted = (current / total * 100).toFixed(2);
                        store.commit(Mutations.SET_PERCENTAGE, {
                            fileName: fileName,
                            extension: 'zip',
                            percent: percentCompleted
                        });
                    }
                }
            )
                .then((response) => {
                    if (response.data) {
                        downloadFromBlob(response.data, fileName, "zip");
                        NotificationDialog.Success(fileName + '.zip ' + getWord("FileDownloadCompleted"));
                    }
                })
                .catch((error) => {
                    const err = new Error(error)
                    BaseApiService.HandleError(error)
                    throw err;
                })
                .finally(() => {
                    store.commit(Mutations.SET_PERCENTAGE, {
                        fileName: '',
                        extension: '',
                        percent: '0.00'
                    });
                })
        },
    }

    public static Settings = {
        Language: {
            GetAll: function () {
                return CustomerApiService.authenticatedClient().get(ServiceConstants.CustomerService.Settings.Language.Name + '/all').catch((err) => {
                    throw new Error(err);
                })
            },
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Language.LanguageListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Language.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("studioLanguageList", "editStudioLanguage", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Language.LanguageDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Language.LanguageDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Language.LanguageDetailPath(id));
            }
        },
        Translation: {
            GetList: function (pagination: Pagination, filters: any = null) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Translation.TranslationListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Translation.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("studioTranslationList", "editStudioTranslation", data.data.key)
                    }
                })
            },
            GetByKey: function (key: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), "Translation/byKey/" + key)
            },
            Update: function (model: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Translation.TranslationDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Translation.TranslationDetailPath(id))
            },
            DeleteByKey: function (key: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), "Translation/byKey/" + key)
            },
        },
        Program: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Program.ProgramListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Program.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("programList", "editProgram", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Program.ProgramDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Program.ProgramDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Program.ProgramDetailPath(id));
            }
        },
        TokenProvider: {
            GetListPaginated: function (pagination: Pagination) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenProvider.TokenProviderListPath(pagination.page!, pagination.pageSize))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenProvider.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPromptWithoutEdit("tokenProviderList")
                    }
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenProvider.TokenProviderDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenProvider.TokenProviderDetailPath(id));
            }
        },
        TokenRegistry: {
            GetListPaginated: function (pagination: Pagination) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenRegistry.TokenRegistryListPath(pagination.page!, pagination.pageSize))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenRegistry.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("tokenRegistryList", "editTokenRegistry", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                console.log(ServiceConstants.CustomerService.Settings.TokenRegistry.TokenRegistryDetailPath(model.id));
                return BaseApiService.Update(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenRegistry.TokenRegistryDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenRegistry.TokenRegistryDetailPath(id))
            },
            GetByCompanyIdAndBaseTypeAsync: function (companyId: string, baseType: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenRegistry.TokenRegistryByCompanyIdAndBaseTypePath(companyId, baseType))
            },
            GetByCompanyId: function (companyId: string) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenRegistry.TokenRegistryByCompanyIdPath(companyId))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.TokenRegistry.TokenRegistryDetailPath(id));
            }
        },
        Document: {
            GetListPaginated: function (pagination: Pagination) {
                return BaseApiService.GetList(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Document.DocumentListPath(pagination.page!, pagination.pageSize))
            },
            Create: function (model: any) {
                return BaseApiService.Create(CustomerApiService.authenticatedClientForFile(), ServiceConstants.CustomerService.Settings.Document.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPromptWithoutEdit("documentList")
                    }
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Document.DocumentDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(CustomerApiService.authenticatedClient(), ServiceConstants.CustomerService.Settings.Document.DocumentDetailPath(id));
            }
        },
    }
}

export default CustomerApiService;